import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check, X } from "lucide-react";

export default function Register() {
  const { register, error, setError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touchedConfirm, setTouchedConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agree, setAgree] = useState(false);
  const navigate = useNavigate();

  const checks = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lowerOrDigit: /[a-z]/.test(password) && /\d/.test(password),
    match: password.length > 0 && password === confirmPassword,
  }), [password, confirmPassword]);

  const passwordOk = checks.length && checks.upper && checks.lowerOrDigit;
  const formValid = name.trim().length >= 2 && email.includes("@") && passwordOk && checks.match && agree;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!checks.match) {
      setError("Passwords don't match");
      return;
    }
    if (!passwordOk) {
      setError("Password must be 8+ chars with uppercase, lowercase and a digit");
      return;
    }
    setSubmitting(true);
    const ok = await register(email, password, name);
    setSubmitting(false);
    if (ok) navigate("/app/dashboard");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#fafaf8]">
      <div className="flex items-center justify-center p-8 order-2 md:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12 md:hidden" data-testid="register-mobile-brand">
            <span className="block w-2 h-2 bg-[#d95d39]" />
            <span className="font-['Outfit'] text-xl font-bold tracking-tight">FinCanvas</span>
          </Link>
          <p className="tc-overline mb-3">Create your account</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight mb-3 leading-tight">
            Set up your studio
          </h1>
          <p className="text-sm text-[#5a5a53] mb-10">
            30 seconds. No credit card. You can change everything later.
          </p>

          <form onSubmit={submit} className="space-y-5" noValidate>
            <div>
              <Label htmlFor="name" className="text-xs tracking-[0.18em] uppercase font-bold text-[#5a5a53]">
                Full name
              </Label>
              <Input
                id="name"
                data-testid="register-name"
                placeholder="Anaya Sharma"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0] focus-visible:ring-[#d95d39]"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.18em] uppercase font-bold text-[#5a5a53]">
                Work email
              </Label>
              <Input
                id="email"
                data-testid="register-email"
                type="email"
                placeholder="you@studio.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0] focus-visible:ring-[#d95d39]"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.18em] uppercase font-bold text-[#5a5a53]">
                Password
              </Label>
              <Input
                id="password"
                data-testid="register-password"
                type="password"
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0] focus-visible:ring-[#d95d39]"
                required
              />
              {password.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid="register-password-strength">
                  <Strength ok={checks.length} text="At least 8 characters" />
                  <Strength ok={checks.upper} text="One uppercase letter" />
                  <Strength ok={checks.lowerOrDigit} text="Lowercase + number" />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="confirm" className="text-xs tracking-[0.18em] uppercase font-bold text-[#5a5a53]">
                Confirm password
              </Label>
              <Input
                id="confirm"
                data-testid="register-confirm-password"
                type="password"
                placeholder="••••••••"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouchedConfirm(true)}
                className={`mt-2 h-11 rounded-md focus-visible:ring-[#d95d39] ${
                  touchedConfirm && confirmPassword && !checks.match
                    ? "border-[#b83a26]"
                    : "border-[#e5e5e0]"
                }`}
                required
              />
              {touchedConfirm && confirmPassword.length > 0 && !checks.match && (
                <p data-testid="register-confirm-mismatch" className="text-[11px] text-[#b83a26] mt-1.5">
                  Passwords don't match
                </p>
              )}
              {confirmPassword.length > 0 && checks.match && (
                <p className="text-[11px] text-[#4a6b53] mt-1.5 flex items-center gap-1">
                  <Check size={11} /> Passwords match
                </p>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 accent-[#d95d39]"
                data-testid="register-tos-checkbox"
              />
              <span className="text-xs text-[#5a5a53] leading-relaxed">
                I agree to FinCanvas's{" "}
                <a className="underline hover:text-[#0a0a0a]" href="#">Terms</a>
                {" "}and{" "}
                <a className="underline hover:text-[#0a0a0a]" href="#">Privacy Policy</a>.
              </span>
            </label>

            {error && (
              <div data-testid="register-error" className="text-xs text-[#b83a26] bg-[#fff4f1] border border-[#f5d5cb] px-3 py-2 rounded-md">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !formValid}
              data-testid="register-submit"
              className="w-full h-11 bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[#5a5a53]">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login" className="text-[#d95d39] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden md:flex flex-col justify-between p-12 bg-[#0a0a0a] text-[#fafaf8] order-1 md:order-2">
        <Link to="/" className="flex items-center gap-2" data-testid="register-brand">
          <span className="block w-2 h-2 bg-[#d95d39]" />
          <span className="font-['Outfit'] text-xl font-bold">FinCanvas</span>
        </Link>
        <div>
          <p className="tc-overline mb-4" style={{ color: "#d95d39" }}>Your stack, simplified</p>
          <h2 className="font-['Outfit'] text-5xl font-bold tracking-tight leading-[0.95]">
            One workspace.
            <br />Zero spreadsheets.
          </h2>
          <ul className="mt-10 space-y-3 text-sm text-[#a3a39b]">
            {[
              "GST-ready invoices in 30 seconds",
              "Old vs New regime — compared instantly",
              "AI tax co-pilot trained for India",
              "PDF invoices that look like art",
              "Bank-grade encryption on your data",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <Check size={14} className="text-[#d95d39]" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-[#5a5a53]">No credit card · Free during beta · Made in India</div>
      </div>
    </div>
  );
}

function Strength({ ok, text }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-[#4a6b53]" : "text-[#a3a39b]"}`}>
      {ok ? <Check size={11} /> : <X size={11} />}
      <span>{text}</span>
    </div>
  );
}
